/**
 * Presentation overrides for the embedded `<thanos-review-widget>`:
 * our dark-mode palette, plus the checkout-specific question above the stars.
 *
 * ## Why this lives here and not in the widget
 *
 * The widget (oppizi-sales-ai `platform/public/widget/review-v1.js`) hardcodes
 * a light palette — white card, `#f7f7f7` disclosure, `#555` copy, a
 * near-black `#1a1a2e` submit button — with no theming hooks: it exposes no
 * CSS custom properties and no `part=` attributes, so `::part()` and variable
 * inheritance are both dead ends. Inside our dark drawer it renders as a stack
 * of white boxes.
 *
 * The one opening: the widget attaches its shadow root with `mode: "open"`,
 * so the host page can reach `element.shadowRoot` and append its own
 * stylesheet. Because our `<style>` lands AFTER the widget's, equal-specificity
 * rules win — no `!important` needed.
 *
 * This is deliberately scoped to OUR embed: the widget on any other host keeps
 * its own look, and nothing here needs a Thanos release. When the widget grows
 * real theming (CSS variables or a `theme` attribute), delete this module and
 * pass the theme through instead.
 */

const STYLE_MARKER = "data-oppizi-widget-theme";

/**
 * Overrides only what the widget's own rules set, keyed to the same selectors.
 * `:host` is reachable from inside the shadow root, which matters because the
 * widget's `all:initial` reset leaves text defaulting to black.
 */
/**
 * Layout fix, applied in BOTH themes: the widget's card caps itself at 420px
 * and paints its own border + background, while our popup wrapper is 460px with
 * its own surface — so ~40px of wrapper stuck out to the right of the card and
 * the two rounded corners disagreed. Let the card fill the wrapper and drop its
 * surface so there is exactly one card, the wrapper's. Padding stays with the
 * card, which is what keeps the content inset.
 */
const BASE_CSS = `
.trw-card{max-width:none;width:100%;border:none;background:transparent}
`;

const DARK_CSS = `
:host{color-scheme:dark}
.trw-card{color:#e2e8f0}
.trw-title{color:#f1f5f9}
.trw-disclosure{background:#1e293b;color:#cbd5e1}
.trw-star{color:#475569}
.trw-star.is-filled{color:#fbbf24}
textarea.trw-text{background:#1e293b;border-color:#475569;color:#e2e8f0}
textarea.trw-text::placeholder{color:#94a3b8}
.trw-counter{color:#94a3b8}
.trw-consent{color:#cbd5e1}
.trw-submit{background:#3fc7fa;color:#0b1220}
.trw-error{color:#f87171}
.trw-success{color:#4ade80}
`;

const HEADING_MARKER = "data-oppizi-widget-heading";

/**
 * Checkout-specific prompt above the stars. It lives here rather than in the
 * widget because it names *this* context ("creating the campaign") — the same
 * widget is embedded elsewhere, where that question would be wrong.
 */
const HEADING_TEXT = "How was your experience creating the campaign?";

const isDarkTheme = (): boolean => document.documentElement.classList.contains("dark");

/**
 * Inserts the prompt directly before the star row, and keeps its colour in step
 * with the theme (white on our dark card, near-black on the light one — a
 * literal white would vanish in light mode).
 */
const applyHeading = (shadow: ShadowRoot): void => {
  const stars = shadow.querySelector(".trw-stars");
  if (!stars?.parentNode) return;

  let heading = shadow.querySelector<HTMLParagraphElement>(`p[${HEADING_MARKER}]`);
  if (!heading) {
    heading = document.createElement("p");
    heading.setAttribute(HEADING_MARKER, "true");
    heading.textContent = HEADING_TEXT;
    heading.style.cssText = "margin:0 0 10px;font-size:14px;font-weight:600;line-height:1.4";
    stars.parentNode.insertBefore(heading, stars);
  }
  heading.style.color = isDarkTheme() ? "#ffffff" : "#0f172a";
};

const applyTheme = (shadow: ShadowRoot): void => {
  // The stylesheet is always present now — BASE_CSS is a layout fix that both
  // themes need; only the palette half is conditional.
  let style = shadow.querySelector<HTMLStyleElement>(`style[${STYLE_MARKER}]`);
  if (!style) {
    style = document.createElement("style");
    style.setAttribute(STYLE_MARKER, "true");
    shadow.appendChild(style);
  }
  const css = isDarkTheme() ? `${BASE_CSS}${DARK_CSS}` : BASE_CSS;
  if (style.textContent !== css) style.textContent = css;
};

/**
 * Keeps the widget's shadow DOM in sync with the app theme for as long as the
 * popup is mounted. Returns a cleanup function.
 *
 * Handles the three ways this can drift:
 *  - the widget script is async, so `shadowRoot` may not exist yet (poll to a deadline);
 *  - the widget builds its DOM by assigning `innerHTML`, which would drop our
 *    node if it ever re-rendered (observe the shadow root and re-apply);
 *  - the user toggles the app theme while the popup is open (observe the root class).
 */
export const syncThanosWidgetEmbed = (element: HTMLElement | null): (() => void) => {
  if (!element) return () => undefined;

  let cancelled = false;
  let frame = 0;
  const deadline = Date.now() + 10000;
  const observers: MutationObserver[] = [];

  const applyAll = (shadow: ShadowRoot): void => {
    applyTheme(shadow);
    applyHeading(shadow);
  };

  const attach = (shadow: ShadowRoot): void => {
    applyAll(shadow);

    // Re-apply if the widget replaces its shadow content (drops our nodes).
    // Also covers the first paint: the star row may not exist on the tick we
    // attach, and any child mutation brings us back to insert the heading.
    const shadowObserver = new MutationObserver(() => applyAll(shadow));
    shadowObserver.observe(shadow, { childList: true, subtree: true });
    observers.push(shadowObserver);

    // Follow app-level theme toggles while the popup stays open.
    const themeObserver = new MutationObserver(() => applyAll(shadow));
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    observers.push(themeObserver);
  };

  const waitForShadowRoot = (): void => {
    if (cancelled) return;
    const shadow = (element as HTMLElement & { shadowRoot: ShadowRoot | null }).shadowRoot;
    if (shadow) {
      attach(shadow);
      return;
    }
    // The custom element may not be upgraded yet (async script). Give up quietly
    // after the deadline — a light-looking widget is far better than a throw.
    if (Date.now() > deadline) return;
    frame = window.requestAnimationFrame(waitForShadowRoot);
  };

  waitForShadowRoot();

  return () => {
    cancelled = true;
    if (frame) window.cancelAnimationFrame(frame);
    observers.forEach((observer) => observer.disconnect());
  };
};
